import type {
  FormConditionalValidation,
  FormField,
  FormInitialValues,
  FormValidations,
} from '@/forms/typings';
import type { ConditionalFieldsGroupFormField } from '@/forms/typings';
import { registerGroupRestrictionValidations } from '@/forms/utils/createAtLeastOneChildWithValueYup';
import { generateValidations } from '@/forms/utils/generateValidations';

import type { FormFieldModuleContext } from '../registry/field-module.types';

export function buildConditionalFieldsGroupInitialState(
  field: FormField,
  values: FormInitialValues,
  validations: FormValidations,
  conditions: FormConditionalValidation[] | undefined,
  ctx: FormFieldModuleContext,
): { initialValues: FormInitialValues; validationFields: FormValidations } {
  let initialValues: FormInitialValues = { ...values };
  let validationFields: FormValidations = { ...validations };

  const f = field as ConditionalFieldsGroupFormField;

  if (f.notApplicableField) {
    initialValues[f.notApplicableField.fieldId] = f.notApplicableField.value;
  }

  // Keyed on the condition field, not on a `${fieldId}_required` flag: this group
  // has no presenter-side writer for that flag, so a rule there could never clear.
  const schema = generateValidations(f, conditions);

  if (schema) {
    validationFields[f.conditionField.fieldId] = schema;
  }

  // Restrictions of matched branch groups whose children were flattened into f.children.
  for (const groupRestriction of f.groupRestrictions ?? []) {
    const branchChildren = f.children.filter(child =>
      child.fieldId.startsWith(`${groupRestriction.groupFieldId}_`),
    );

    registerGroupRestrictionValidations(
      initialValues,
      validationFields,
      groupRestriction.groupFieldId,
      branchChildren,
      groupRestriction.restriction,
      conditions,
    );
  }

  const conditionValuesAndValidations = ctx.recurse(
    f.conditionField,
    initialValues,
    validationFields,
    conditions,
  );

  initialValues = {
    ...initialValues,
    ...conditionValuesAndValidations.initialValues,
  };

  validationFields = {
    ...validationFields,
    ...conditionValuesAndValidations.validationFields,
  };

  for (const child of f.children) {
    const childValuesAndValidations = ctx.recurse(
      child,
      initialValues,
      validationFields,
      conditions,
    );

    initialValues = {
      ...initialValues,
      ...childValuesAndValidations.initialValues,
    };

    validationFields = {
      ...validationFields,
      ...childValuesAndValidations.validationFields,
    };
  }

  if (f.naDetails && f.notApplicableField) {
    const combinedConditions: FormConditionalValidation[] = [
      { type: 'not-applicable', relatedFieldId: f.notApplicableField.fieldId },
      ...(conditions ?? []),
    ];

    const naValuesAndValidations = ctx.recurse(
      f.naDetails,
      initialValues,
      validationFields,
      combinedConditions,
    );

    initialValues = {
      ...initialValues,
      ...naValuesAndValidations.initialValues,
    };

    validationFields = {
      ...validationFields,
      ...naValuesAndValidations.validationFields,
    };
  }

  return { initialValues, validationFields };
}
